import { Link, useLocation } from "react-router-dom";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
} from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import {
  LayoutDashboard,
  ShoppingBag,
  Users,
  Package,
  Settings,
  LogOut,
  BarChart,
  LifeBuoy,
  ChevronDown,
  Plus,
  ImageIcon,
  FileText,
  Edit,
  Mail,
  Percent,
  UploadCloud,
  MessageCircle,
  ArrowLeftRight,
  Info,
  Star,
  Scale,
  HelpCircle,
  CreditCard,
  Shield,
  Activity,
  Gift,
} from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import Logo from "../../../assets/images/logo/logo.png";
import { useAdminAuth } from "@/context/Admin";

export default function AdminDashboardSidebar() {
  const { logout, admin } = useAdminAuth();
  const location = useLocation();
  const isSuperAdmin = admin?.role === "super_admin";

  const mainNavigation = [
    { title: "Overview", to: "/admin", icon: LayoutDashboard },
    // { title: "Orders", to: "/admin/orders/list", icon: ShoppingBag, badge: 12 },
    { title: "Users", to: "/admin/users", icon: Users },
    { title: "Send Mail", to: "/admin/mail", icon: Mail },
    { title: "Media Library", to: "/admin/library", icon: ImageIcon },
    { title: "Media Upload", to: "/admin/library/upload", icon: UploadCloud },
    { title: "Coupons", to: "/admin/coupons", icon: Percent },
    { title: "Analytics", to: "/admin/overview", icon: BarChart },
    {
      title: "Support Tickets",
      to: "/admin/support",
      icon: LifeBuoy,
      badge: 3,
    },
    { title: "Referrals", to: "/admin/referrals", icon: Gift },
    ...(isSuperAdmin
      ? [
          { title: "Login History", to: "/admin/login-history", icon: Shield },
          { title: "Activity Log", to: "/admin/activity-log", icon: Activity },
        ]
      : []),
  ];

  const productManagement = [
    { title: "All Products", to: "/admin/products/list", icon: Package },
    { title: "Add New Product", to: "/admin/products/create", icon: Plus },
    // { title: "Category", to: "/admin/products/create", icon: Plus },
  ];

  const contentManagement = [
    { title: "All Blog Posts", to: "/admin/blog/list", icon: FileText },
    { title: "Create Blog Post", to: "/admin/blog/create", icon: Edit },
    {
      title: "Blog Comments",
      to: "/admin/blog/comments",
      icon: MessageCircle,
      badge: 5,
    },
    { title: "About Us", to: "/admin/about", icon: Info },
    { title: "Reviews", to: "/admin/reviews", icon: Star },
    { title: "Legal Pages", to: "/admin/legal", icon: Scale },
    { title: "FAQ", to: "/admin/faq", icon: HelpCircle },
  ];

  const OrderManagement = [
    { title: "All Orders", to: "/admin/orders/list", icon: Package },
    { title: "Create Order", to: "/admin/orders/create", icon: Plus },
    { title: "Return Requests", to: "/admin/returns", icon: ArrowLeftRight },
    { title: "Transactions", to: "/admin/transactions", icon: CreditCard },
    // { title: "Skin Scans", to: "/admin/skin-scans", icon: Activity },
  ];

  // Collect all route paths for matching
  const allRoutes = [
    ...mainNavigation.map((item) => item.to),
    ...productManagement.map((item) => item.to),
    ...contentManagement.map((item) => item.to),
    ...OrderManagement.map((item) => item.to),
    "/admin/settings",
    "/admin/login-history",
    "/admin/activity-log",
    "/admin/referrals",
    // "/admin/skin-scans"
  ];

  // Helper function to check if a route is active
  const isActiveRoute = (routePath) => {
    const currentPath = location.pathname;

    // Exact match for root admin route
    if (routePath === "/admin") {
      return currentPath === "/admin";
    }

    // Exact match
    if (currentPath === routePath) {
      return true;
    }

    // Check if current path starts with this route path
    if (!currentPath.startsWith(routePath + "/")) {
      return false;
    }

    // If it's a prefix match, ensure this is the longest matching route
    // This prevents parent routes from matching when on a child route
    const matchingRoutes = allRoutes.filter(
      (route) => currentPath.startsWith(route + "/") || currentPath === route,
    );

    // Find the longest matching route
    const longestMatch = matchingRoutes.reduce(
      (longest, route) => (route.length > longest.length ? route : longest),
      "",
    );

    // Only return true if this route is the longest match
    return routePath === longestMatch;
  };

  return (
    <Sidebar collapsible="icon" side="left">
      <SidebarHeader>
        <Link to="/admin" className="flex items-center gap-1.5 p-1">
          <img
            src={Logo}
            alt="logo"
            style={{ width: "120px", height: "40px", align: "center" }}
          />
        </Link>
      </SidebarHeader>

      <Separator />

      <SidebarContent className="scrollbar-hide">
        {/* Main Navigation */}
        <SidebarGroup>
          <SidebarGroupLabel className="group-data-[state=collapsed]:hidden">
            Dashboard
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {mainNavigation.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton
                    asChild
                    tooltip={item.title}
                    isActive={isActiveRoute(item.to)}
                  >
                    <Link to={item.to}>
                      <item.icon />
                      <span className="group-data-[state=collapsed]:hidden">
                        {item.title}
                      </span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Product Management */}
        <Collapsible defaultOpen className="group/collapsible">
          <SidebarGroup>
            <SidebarGroupLabel asChild>
              <CollapsibleTrigger className="w-full flex items-center justify-between">
                <span className="group-data-[state=collapsed]:hidden">
                  Product Management
                </span>
                <ChevronDown className="ml-auto h-4 w-4 transition-transform group-data-[state=collapsed]:hidden group-data-[state=open]/collapsible:rotate-180" />
              </CollapsibleTrigger>
            </SidebarGroupLabel>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu>
                  {productManagement.map((item) => (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton
                        asChild
                        tooltip={item.title}
                        isActive={isActiveRoute(item.to)}
                      >
                        <Link to={item.to}>
                          <item.icon />
                          <span className="group-data-[state=collapsed]:hidden">
                            {item.title}
                          </span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>

        {/* Content Management */}
        <Collapsible defaultOpen className="group/collapsible">
          <SidebarGroup>
            <SidebarGroupLabel asChild>
              <CollapsibleTrigger className="w-full flex items-center justify-between">
                <span className="group-data-[state=collapsed]:hidden">
                  Content Management
                </span>
                <ChevronDown className="ml-auto h-4 w-4 transition-transform group-data-[state=collapsed]:hidden group-data-[state=open]/collapsible:rotate-180" />
              </CollapsibleTrigger>
            </SidebarGroupLabel>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu>
                  {contentManagement.map((item) => (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton
                        asChild
                        tooltip={item.title}
                        isActive={isActiveRoute(item.to)}
                      >
                        <Link to={item.to}>
                          <item.icon />
                          <span className="group-data-[state=collapsed]:hidden">
                            {item.title}
                          </span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>

        {/* Order Management */}
        <Collapsible defaultOpen className="group/collapsible">
          <SidebarGroup>
            <SidebarGroupLabel asChild>
              <CollapsibleTrigger className="w-full flex items-center justify-between">
                <span className="group-data-[state=collapsed]:hidden">
                  Order Management
                </span>
                <ChevronDown className="ml-auto h-4 w-4 transition-transform group-data-[state=collapsed]:hidden group-data-[state=open]/collapsible:rotate-180" />
              </CollapsibleTrigger>
            </SidebarGroupLabel>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu>
                  {OrderManagement.map((item) => (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton
                        asChild
                        tooltip={item.title}
                        isActive={isActiveRoute(item.to)}
                      >
                        <Link to={item.to}>
                          <item.icon />
                          <span className="group-data-[state=collapsed]:hidden">
                            {item.title}
                          </span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>
      </SidebarContent>

      <Separator />

      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              tooltip="Settings"
              isActive={isActiveRoute("/admin/settings")}
            >
              <Link to="/admin/settings">
                <Settings />
                <span className="group-data-[state=collapsed]:hidden">
                  Settings
                </span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <SidebarMenuButton tooltip="Log Out">
                  <div className="flex items-center gap-2 cursor-pointer">
                    <LogOut />
                    <span className="group-data-[state=collapsed]:hidden">
                      Log Out
                    </span>
                  </div>
                </SidebarMenuButton>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                  <AlertDialogDescription>
                    You will be logged out and redirected to the login page.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={logout}>
                    Log Out
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>

      <SidebarRail />
    </Sidebar>
  );
}
